import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  #collectionFilterRequestId = 0;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.addEventListener('click', this.#handleCollectionFilterClick);
    this.addEventListener('pointerover', this.#handleCollectionFilterPrefetch);
    this.addEventListener('focusin', this.#handleCollectionFilterPrefetch);
    this.setAttribute('initialized', '');

    requestIdleCallback(() => this.#prefetchCollectionFilterLinks());
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.removeEventListener('click', this.#handleCollectionFilterClick);
    this.removeEventListener('pointerover', this.#handleCollectionFilterPrefetch);
    this.removeEventListener('focusin', this.#handleCollectionFilterPrefetch);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles collection filter pill navigation with section rendering.
   *
   * @param {MouseEvent} event
   */
  #handleCollectionFilterClick = async (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    const target = event.target;
    if (!(target instanceof Element)) return;

    const link = target.closest('.collection-filter-pills__item');
    if (!(link instanceof HTMLAnchorElement)) return;
    if (link.target && link.target !== '_self') return;
    if (link.classList.contains('collection-filter-pills__item--active')) return;

    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin) return;

    event.preventDefault();

    try {
      const requestId = ++this.#collectionFilterRequestId;

      this.#setActiveCollectionFilterPill(url);
      this.#setCollectionFilterLoading(true);

      const html = await this.#getCollectionSectionHTMLWithFilterPills(url);
      if (requestId !== this.#collectionFilterRequestId) return;

      await startViewTransition(() => morphSection(this.sectionId, html), ['product-grid']);
    } catch (_error) {
      window.location.href = url.toString();
    } finally {
      this.#setCollectionFilterLoading(false);
    }
  };

  /**
   * Updates the product grid loading state while collection filter pills fetch.
   *
   * @param {boolean} isLoading
   */
  #setCollectionFilterLoading(isLoading) {
    const grid = this.querySelector('.main-collection-grid');
    if (!(grid instanceof HTMLElement)) return;

    grid.toggleAttribute('aria-busy', isLoading);
    grid.classList.toggle('main-collection-grid--loading', isLoading);
  }

  /**
   * Gets collection HTML while preserving the existing filter pills through the morph.
   *
   * @param {URL} url
   * @returns {Promise<string>}
   */
  async #getCollectionSectionHTMLWithFilterPills(url) {
    const filterPills = this.querySelector('.collection-filter-pills');
    const sectionHTML = await sectionRenderer.getSectionHTML(this.sectionId, !Shopify.designMode, url);

    return filterPills instanceof HTMLElement ? this.#injectCollectionFilterPills(sectionHTML, filterPills, url) : sectionHTML;
  }

  /**
   * Adds the current filter pills to section-rendered HTML when the target collection omits them.
   *
   * @param {string} sectionHTML
   * @param {HTMLElement} filterPills
   * @param {URL} activeUrl
   * @returns {string}
   */
  #injectCollectionFilterPills(sectionHTML, filterPills, activeUrl) {
    const fragment = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const collectionWrapper = fragment.querySelector('results-list .collection-wrapper');
    if (!collectionWrapper || collectionWrapper.querySelector('.collection-filter-pills')) return sectionHTML;

    const nextFilterPills = filterPills.cloneNode(true);
    if (!(nextFilterPills instanceof HTMLElement)) return sectionHTML;

    for (const item of nextFilterPills.querySelectorAll('.collection-filter-pills__item')) {
      if (!(item instanceof HTMLAnchorElement)) continue;

      const itemUrl = new URL(item.dataset.url || item.href, window.location.href);
      const isActive = itemUrl.pathname === activeUrl.pathname;

      if (!item.dataset.url) {
         item.dataset.url = item.href;
      }

      item.classList.toggle('collection-filter-pills__item--active', isActive);
      item.toggleAttribute('aria-current', isActive);
      if (isActive) {
        item.setAttribute('aria-current', 'page');
        item.style.pointerEvents = 'none';
        item.setAttribute('disabled', '');
        item.setAttribute('aria-disabled', 'true');
        item.removeAttribute('href');
      } else {
        item.style.pointerEvents = '';
        item.removeAttribute('disabled');
        item.removeAttribute('aria-disabled');
        item.href = item.dataset.url;
      }
    }

    collectionWrapper.prepend(nextFilterPills);

    return fragment.body.innerHTML;
  }

  /**
   * Updates the visible selected pill before the AJAX request finishes.
   *
   * @param {URL} activeUrl
   */
  #setActiveCollectionFilterPill(activeUrl) {
    for (const item of this.querySelectorAll('.collection-filter-pills__item')) {
      if (!(item instanceof HTMLAnchorElement)) continue;

      const itemUrl = new URL(item.dataset.url || item.href, window.location.href);
      const isActive = itemUrl.pathname === activeUrl.pathname;

      if (!item.dataset.url) {
         item.dataset.url = item.href;
      }

      item.classList.toggle('collection-filter-pills__item--active', isActive);
      item.toggleAttribute('aria-current', isActive);
      if (isActive) {
        item.setAttribute('aria-current', 'page');
        item.style.pointerEvents = 'none';
        item.setAttribute('disabled', '');
        item.setAttribute('aria-disabled', 'true');
        item.removeAttribute('href');
      } else {
        item.style.pointerEvents = '';
        item.removeAttribute('disabled');
        item.removeAttribute('aria-disabled');
        item.href = item.dataset.url;
      }
    }
  }

  /**
   * Prefetches a pill target when users hover/focus it.
   *
   * @param {Event} event
   */
  #handleCollectionFilterPrefetch = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const link = target.closest('.collection-filter-pills__item');
    if (!(link instanceof HTMLAnchorElement)) return;

    this.#prefetchCollectionFilterURL(new URL(link.href, window.location.href));
  };

  /**
   * Prefetches all collection pill targets after initial page work settles.
   */
  #prefetchCollectionFilterLinks() {
    for (const link of this.querySelectorAll('.collection-filter-pills__item')) {
      if (!(link instanceof HTMLAnchorElement)) continue;
      this.#prefetchCollectionFilterURL(new URL(link.href, window.location.href));
    }
  }

  /**
   * Warms the section renderer cache for collection filter targets.
   *
   * @param {URL} url
   */
  #prefetchCollectionFilterURL(url) {
    if (url.origin !== window.location.origin) return;
    if (url.pathname === window.location.pathname && url.search === window.location.search) return;

    sectionRenderer.getSectionHTML(this.sectionId, !Shopify.designMode, url).catch(() => {});
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
